import React, { useEffect, useRef, useState } from "react";
import api, { apiError, API } from "@/lib/api";
import { toast } from "sonner";
import { MessageCircle, X, Send, Loader2, LifeBuoy, Bot } from "lucide-react";

const QUICK_QUESTIONS = [
  "How does calling work?",
  "How do I add money?",
  "What are travel eSIMs?",
  "Why does my own phone ring first?",
];

function getSessionId() {
  let sid = localStorage.getItem("zani_chat_sid");
  if (!sid) {
    sid = `web-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem("zani_chat_sid", sid);
  }
  return sid;
}

export const SupportChat = ({ raised = false, hideLauncher = false }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm Zeep, your ZEEPTEL assistant 👋 Ask me anything about calling, top-ups or travel eSIMs." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showEscalate, setShowEscalate] = useState(false);
  const [contactEmail, setContactEmail] = useState("");
  const [escalateMsg, setEscalateMsg] = useState("");
  const [escalating, setEscalating] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    const openHandler = () => setOpen(true);
    window.addEventListener("zani-open", openHandler);
    return () => window.removeEventListener("zani-open", openHandler);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, showEscalate]);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try { const a = new Audio("/sounds/02_zeeptel_thinking.wav"); a.volume = 0.25; a.play().catch(() => {}); } catch { /* no audio */ }
    try {
      const controller = new AbortController();
      const resp = await fetch(`${API}/support/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: getSessionId(), message: msg }),
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) throw new Error("stream unavailable");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let started = false;
      let escalateFlag = false;
      let streamError = null;
      let gotBytes = false;
      // watchdog: first byte normally arrives <1s; if nothing after 3.5s the stream is stalled
      // (prod LB intermittently delays SSE ~28s) — abort fast and use the non-streaming fallback
      const watchdog = setTimeout(() => {
        if (!gotBytes) controller.abort();
      }, 3500);
      for (;;) {
        const { done, value } = await reader.read();
        gotBytes = true;
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          const line = p.trim();
          if (!line.startsWith("data:")) continue;
          const data = JSON.parse(line.slice(5));
          if (data.error) streamError = data.error;
          if (data.t) {
            if (!started) {
              started = true;
              clearTimeout(watchdog);
              setSending(false);
              setMessages((m) => [...m, { role: "assistant", content: data.t }]);
            } else {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + data.t };
                return c;
              });
            }
          }
          if (data.done) escalateFlag = !!data.escalate;
        }
      }
      clearTimeout(watchdog);
      if (streamError && !started) throw new Error(streamError);
      if (escalateFlag) {
        setMessages((m) => {
          const c = [...m];
          c[c.length - 1] = { ...c[c.length - 1], escalate: true };
          return c;
        });
        setShowEscalate(true);
      }
    } catch (err) {
      try {
        const { data } = await api.post("/support/chat", { session_id: getSessionId(), message: msg }, { timeout: 22000 });
        setMessages((m) => [...m, { role: "assistant", content: data.reply, escalate: data.escalate }]);
        if (data.escalate) setShowEscalate(true);
      } catch {
        setMessages((m) => [...m, { role: "assistant", content: "Sorry, I hit a snag — please try again, or contact our support team below.", escalate: true }]);
      }
    } finally {
      setSending(false);
    }
  };

  const escalate = async () => {
    if (!contactEmail.trim()) {
      toast.error("Please enter your email so we can reply");
      return;
    }
    setEscalating(true);
    try {
      await api.post("/support/escalate", {
        session_id: getSessionId(),
        email: contactEmail.trim(),
        message: escalateMsg.trim(),
      });
      setShowEscalate(false);
      setEscalateMsg("");
      setMessages((m) => [...m, { role: "assistant", content: "✅ Your request was sent to our support team — we'll email you back soon." }]);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setEscalating(false);
    }
  };

  return (
    <>
      {!open && !hideLauncher && (
        <button
          onClick={() => setOpen(true)}
          data-testid="support-chat-open"
          aria-label="Open support chat"
          className={`ai-glow-btn fixed right-4 z-[60] h-12 w-12 md:h-14 md:w-14 rounded-full text-white flex items-center justify-center hover:scale-105 transition-transform ${raised ? "bottom-20 md:bottom-5" : "bottom-5"}`}
        >
          <MessageCircle className="h-6 w-6" />
        </button>
      )}

      {open && (
        <div
          className={`fixed right-4 z-[60] w-[min(380px,calc(100vw-2rem))] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden ${
            raised ? "bottom-20 md:bottom-5 h-[min(560px,calc(100vh-10rem))]" : "bottom-5 h-[min(560px,calc(100vh-6rem))]"}`}
          data-testid="support-chat-panel"
        >
          <div className="bg-[#1E3A8A] text-white px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-white/15 flex items-center justify-center">
                <Bot className="h-4 w-4" />
              </div>
              <div>
                <div className="font-bold text-sm leading-tight">Zeep — ZEEPTEL assistant</div>
                <div className="text-[11px] text-blue-200">Typically replies instantly</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="support-chat-close" aria-label="Close chat" className="p-1.5 rounded-full hover:bg-white/15">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-slate-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "bg-[#1E3A8A] text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"}`}>
                  {m.content}
                  {m.escalate && !showEscalate && (
                    <button onClick={() => setShowEscalate(true)} data-testid="support-escalate-inline"
                      className="mt-2 flex items-center gap-1.5 text-xs font-bold text-[#E11D48] hover:underline">
                      <LifeBuoy className="h-3.5 w-3.5" /> Contact the support team
                    </button>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start" data-testid="zani-typing">
                <div className="bg-white border border-slate-200 px-3.5 py-2.5 rounded-2xl rounded-bl-md flex items-center gap-2">
                  <span className="flex gap-1">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#4285F4] animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-[#9B72CB] animate-bounce" style={{ animationDelay: "120ms" }} />
                    <span className="h-1.5 w-1.5 rounded-full bg-[#D96570] animate-bounce" style={{ animationDelay: "240ms" }} />
                  </span>
                  <span className="text-xs text-slate-400">Zeep is typing…</span>
                </div>
              </div>
            )}

            {showEscalate && (
              <div className="bg-white border-2 border-[#E11D48]/30 rounded-2xl p-3.5 space-y-2" data-testid="support-escalate-form">
                <div className="text-sm font-bold flex items-center gap-1.5"><LifeBuoy className="h-4 w-4 text-[#E11D48]" /> Send to our support team</div>
                <input
                  type="email" placeholder="Your email" value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  data-testid="support-escalate-email"
                  className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-[#1E3A8A]/30"
                />
                <textarea
                  placeholder="Describe your issue (optional — your chat is attached automatically)"
                  value={escalateMsg} onChange={(e) => setEscalateMsg(e.target.value)} rows={2}
                  data-testid="support-escalate-message"
                  className="w-full text-sm border border-slate-200 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 resize-none"
                />
                <div className="flex gap-2">
                  <button onClick={escalate} disabled={escalating} data-testid="support-escalate-send"
                    className="flex-1 py-2 rounded-xl bg-[#E11D48] text-white text-sm font-semibold hover:bg-[#BE123C] disabled:opacity-60 inline-flex items-center justify-center gap-1.5">
                    {escalating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send
                  </button>
                  <button onClick={() => setShowEscalate(false)} className="px-3 py-2 rounded-xl border border-slate-200 text-sm text-slate-500">
                    Cancel
                  </button>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-slate-100 bg-white p-3 shrink-0">
            {messages.length <= 1 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {QUICK_QUESTIONS.map((q) => (
                  <button key={q} onClick={() => send(q)} data-testid="support-quick-question"
                    className="text-xs px-2.5 py-1.5 rounded-full bg-blue-50 text-[#1E3A8A] font-medium hover:bg-blue-100 transition-colors">
                    {q}
                  </button>
                ))}
                <button onClick={() => setShowEscalate(true)} data-testid="support-quick-human"
                  className="text-xs px-2.5 py-1.5 rounded-full bg-red-50 text-[#E11D48] font-medium hover:bg-red-100 transition-colors">
                  Talk to a human
                </button>
              </div>
            )}
            <div className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask me anything…"
                data-testid="support-chat-input"
                className="flex-1 text-sm border border-slate-200 rounded-xl px-3.5 py-2.5 outline-none focus:ring-2 focus:ring-[#1E3A8A]/30"
              />
              <button onClick={() => send()} disabled={sending || !input.trim()} data-testid="support-chat-send"
                aria-label="Send message"
                className="h-10 w-10 rounded-xl bg-[#1E3A8A] text-white flex items-center justify-center hover:opacity-90 disabled:opacity-40">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
